import type { FinanceProposalSnapshotRow } from '@/services/financeService'
import { formatCurrency } from '@/utils/format-currency'

export const FORM_FIELD_LABELS: Record<string, string> = {
  summary: 'Resumo',
  description: 'Observações',
  email: 'E-mail',
  cnpj: 'CNPJ',
  razaoSocial: 'Razão social',
  endereco: 'Endereço',
  plataforma: 'Plataforma',
  servicosContratados: 'Serviços contratados',
  valorTotal: 'Valor total',
  valorMensal: 'Valor mensal',
  valorSetup: 'Valor setup',
  condicaoPagamento: 'Condição de pagamento',
  contractStartDate: 'Data inicial do contrato',
  firstPaymentDate: 'Data inicial de pagamento',
  closerNome: 'Closer',
  closerEmail: 'E-mail do closer',
  nome: 'Nome',
  contatoNome: 'Contato — nome',
  contatoTelefone: 'Contato — telefone / WhatsApp',
  contractDeliveryMode: 'Formato do contrato',
  contractDeliveryLabel: 'Entrega solicitada',
  contractSignatureLink: 'Link para assinatura',
  responsavelAssinatura: 'Responsável pela assinatura',
  responsavelFinanceiro: 'Responsável financeiro',
  assunto: 'Assunto',
  descricao: 'Descrição',
  urgencia: 'Urgência',
  remuneracaoMensal: 'Remuneração mensal prevista',
  valorEstimado: 'Valor estimado',
  valorAntecipar: 'Valor a antecipar',
  valor: 'Valor',
}

const CURRENCY_FIELD_KEYS = new Set([
  'valor',
  'valorTotal',
  'valorMensal',
  'valorSetup',
  'valorEstimado',
  'valorAntecipar',
  'remuneracaoMensal',
])

const RESPONSAVEL_FIELD_KEYS = new Set(['responsavelAssinatura', 'responsavelFinanceiro'])

export function isResponsavelField(key: string): boolean {
  return RESPONSAVEL_FIELD_KEYS.has(key)
}

export function getFormFieldLabel(key: string): string {
  return FORM_FIELD_LABELS[key] ?? key.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2')
}

export function propostaToSnapshot(value: unknown): FinanceProposalSnapshotRow | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null
  const raw = value as unknown as Record<string, unknown>

  let items = Array.isArray(raw.items) ? raw.items : []
  let dados = raw.dados

  if (items.length === 0 && typeof dados === 'string') {
    try {
      const parsed = JSON.parse(dados) as unknown as Record<string, unknown>
      if (Array.isArray(parsed.items)) {
        items = parsed.items
        dados = parsed
      }
    } catch {
      // mantém dados como string para parseFinanceProposalSnapshot
    }
  }

  const hasItems = items.length > 0
  const hasDados = dados != null && dados !== ''
  const hasTotal = typeof raw.totalCentavos === 'number'
  if (!hasItems && !hasDados && !hasTotal) return null

  return {
    id: 'form-proposta',
    versao: 1,
    totalFinal: hasTotal ? (raw.totalCentavos as number) / 100 : null,
    pdfUrl: typeof raw.link === 'string' ? raw.link : null,
    createdAt: new Date().toISOString(),
    payloadJson: {
      items,
      dados: dados as string | Record<string, unknown> | undefined,
      link: typeof raw.link === 'string' ? raw.link : null,
    },
  }
}

export function isProposalPayload(value: unknown): boolean {
  return propostaToSnapshot(value) != null
}

export function isNestedRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value) && !isProposalPayload(value)
}

export function parseNumericField(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed) return null
    const normalized = trimmed.replace(/\./g, '').replace(',', '.')
    const num = Number(normalized)
    return Number.isFinite(num) ? num : null
  }
  return null
}

export function formatScalarField(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não'
  if (typeof value === 'number') return String(value)
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (trimmed === 'pdf') return 'PDF do contrato'
    if (trimmed === 'link') return 'Link do contrato'
    if (
      (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
      try {
        JSON.parse(trimmed)
        return '—'
      } catch {
        return value
      }
    }
    return value
  }
  return String(value)
}

export function formatFormFieldValue(key: string, value: unknown): string {
  if (CURRENCY_FIELD_KEYS.has(key)) {
    const num = parseNumericField(value)
    if (num != null) return formatCurrency(num)
  }
  return formatScalarField(value)
}

export function getDisplayFormEntries(formData: Record<string, unknown>) {
  const propostaRaw = formData.proposta
  const propostaSnapshot = propostaToSnapshot(propostaRaw)

  const entries = Object.entries(formData).filter(([key, value]) => {
    if (value === null || value === undefined || value === '') return false
    if (key === 'proposta' && propostaSnapshot) return false
    return true
  })

  return { entries, propostaSnapshot }
}
